/**
 * Review UI for a single NCCN evaluation criterion (pathway item).
 *
 * Renders as a stack of separate boxes: a criteria box (position badge,
 * statement, AI verdict, Correct/Incorrect controls, and the conditional
 * feedback form), followed by the AI reasoning box and the evidence sections.
 * @module CriterionReview
 */
import { ActionChip, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useMemo, useState } from "react";
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import { EvaluationItem } from "../../../data-model/nccnGuidelines";
import { ThumbsDownIcon } from "../../../svg/thumbs-down-icon";
import { ThumbsUpIcon } from "../../../svg/thumbs-up-icon";
import { CriterionOutcome } from "../analytics/mappers";
import { MedicalNecessityAnalytics } from "../analytics/useMedicalNecessityAnalytics";
import {
  buildChunkLocationsMap,
  withParsedLocations,
} from "../utils/evidenceText";
import { FeedbackInput, readItemFeedback } from "../utils/nccnReviewFeedback";
import { AiReasoningBox } from "./AiReasoningBox";
import { CriterionFeedbackForm } from "./CriterionFeedbackForm";
import { EvidenceSection } from "./EvidenceSection";
import { IncorrectCommentSummary } from "./IncorrectCommentSummary";
import { IncorrectEvidenceSummary } from "./IncorrectEvidenceSummary";
import { IncorrectLogicSummary } from "./IncorrectLogicSummary";
import { AiVerdictBadge } from "./nccnVerdictBadge";

/** Props for {@link CriterionReview}. */
interface CriterionReviewProps {
  item: EvaluationItem;
  positionLabel: string;
  isSaving: boolean;
  onMarkCorrect: () => void;
  onSaveFeedback: (input: FeedbackInput) => void;
  /** Fires on every Correct / Incorrect selection (including reselection). */
  onOutcomeSelected?: (outcome: CriterionOutcome) => void;
  /** Fires when Save Feedback is clicked with the Incorrect form payload. */
  onSaveFeedbackClicked?: (input: FeedbackInput) => void;
  /** Fires when Cancel is clicked on the Incorrect feedback form. */
  onFeedbackCancel?: () => void;
  /** Guideline the criterion belongs to (analytics location). */
  guideline: NCCNEvaluationRecord;
  /** One-based display index of the guideline. */
  guidelineIndex: number;
  /** One-based index of the criterion within the guideline. */
  criterionIndex: number;
  /** Analytics facade for firing proof / comment events. */
  analytics: MedicalNecessityAnalytics;
}

/**
 * Renders the full review UI for one criterion.
 *
 * @param props - The item plus saving flag and correct/feedback handlers
 * @returns The rendered criterion review
 */
export const CriterionReview: React.FC<CriterionReviewProps> = ({
  item,
  positionLabel,
  isSaving,
  onMarkCorrect,
  onSaveFeedback,
  onOutcomeSelected,
  onSaveFeedbackClicked,
  onFeedbackCancel,
  guideline,
  guidelineIndex,
  criterionIndex,
  analytics,
}) => {
  const parsed = readItemFeedback(item);

  // Resolve this item's `DocChunk#N` citations to a page/line in the clinical
  // notes so the evidence chips can deep-link the left-hand PDF. Memoized on the
  // item so the map is rebuilt only when the reviewed criterion changes.
  const chunkLocations = useMemo(
    () =>
      withParsedLocations(
        buildChunkLocationsMap(
          item.chunks_used ?? [],
          item.chunks_used_locations,
        ),
      ),
    [item.chunks_used, item.chunks_used_locations],
  );
  // Saved PDF references have no read-only summary: the criterion opens
  // straight into the capture form, seeded with whatever was stored. Deriving
  // this once on mount (rather than from the live reference list) keeps the
  // form on screen after the reviewer deletes every annotation, so they can
  // capture a replacement without reselecting Incorrect.
  const [isFormOpen, setIsFormOpen] = useState(
    () =>
      parsed.isIncorrect &&
      parsed.kind === "incorrect_reference" &&
      parsed.proofs.length > 0,
  );
  // Local source of truth for which verdict chip is highlighted, so switching
  // verdicts always deselects the other one immediately (before any save
  // resolves). Seeded from whatever verdict is currently stored on the item.
  const [selection, setSelection] = useState<"correct" | "incorrect" | null>(
    parsed.isCorrect ? "correct" : parsed.isIncorrect ? "incorrect" : null,
  );

  const handleSave = (input: FeedbackInput) => {
    onSaveFeedbackClicked?.(input);
    onSaveFeedback(input);
    // Reference writes (including the clear that follows removing the last
    // annotation) keep the capture form open so the reviewer can carry on.
    const isReferenceSessionWrite =
      input.type === "incorrect_reference" || input.type === "unreviewed";
    if (!isReferenceSessionWrite) setIsFormOpen(false);
  };

  const openIncorrectForm = () => {
    setSelection("incorrect");
    setIsFormOpen(true);
    onOutcomeSelected?.("incorrect");
  };

  // Once incorrect-evidence feedback is saved, surface a read-only summary of
  // the flagged facts and their corrections instead of the live form.
  const showSavedEvidence =
    !isFormOpen &&
    parsed.isIncorrect &&
    parsed.kind === "incorrect_evidence" &&
    parsed.flagged.length > 0;
  // Likewise, surface a read-only summary of saved incorrect-logic feedback.
  const showSavedLogic =
    !isFormOpen &&
    parsed.isIncorrect &&
    parsed.kind === "incorrect_logic" &&
    parsed.correctLogic.trim().length > 0;
  // And of a saved free-text comment.
  const showSavedComment =
    !isFormOpen &&
    parsed.isIncorrect &&
    parsed.kind === "incorrect_comment" &&
    parsed.comment.trim().length > 0;
  const showFeedbackForm = selection === "incorrect" && isFormOpen;
  const statementLines = (item.path ?? "").split("\n").filter(Boolean);

  return (
    <div className="flex flex-col gap-4 self-stretch">
      <div className="flex flex-col items-start gap-3 self-stretch">
        <div className="flex items-center gap-2">
          <Tags
            label={positionLabel}
            backgroundColor="#F5F5F5"
            textColor="#374151"
          />
          <span className="text-xs font-bold leading-[18px] text-[#6B6B6B]">
            Criteria
          </span>
        </div>

        <div className="flex flex-col gap-1 self-stretch">
          {statementLines.map((line, index) => (
            <p
              key={index}
              className="self-stretch text-[0.75rem] font-normal leading-5 text-[#0F0F0F]"
            >
              {line}
            </p>
          ))}
        </div>

        <div className="flex items-center gap-3 self-stretch">
          <AiVerdictBadge verdict={item.verdict} />
          <div className="flex items-center gap-3">
            <ActionChip
              label="Correct"
              icon={<ThumbsUpIcon color="currentColor" />}
              active={selection === "correct"}
              activeBackgroundColor="#E6F3F0"
              activeTextColor="#005D49"
              disabled={isSaving}
              onClick={() => {
                setSelection("correct");
                setIsFormOpen(false);
                onOutcomeSelected?.("correct");
                onMarkCorrect();
              }}
            />
            <ActionChip
              label="Incorrect"
              icon={<ThumbsDownIcon color="currentColor" />}
              active={selection === "incorrect"}
              activeBackgroundColor="#FFE8E8"
              activeTextColor="#CC0300"
              disabled={isSaving}
              onClick={openIncorrectForm}
            />
          </div>
        </div>

        {showFeedbackForm && (
          <CriterionFeedbackForm
            key={`${item.id}-form`}
            initial={parsed}
            isSaving={isSaving}
            guideline={guideline}
            guidelineIndex={guidelineIndex}
            item={item}
            criterionIndex={criterionIndex}
            analytics={analytics}
            onCancel={() => {
              onFeedbackCancel?.();
              setIsFormOpen(false);
              setSelection(
                parsed.isCorrect
                  ? "correct"
                  : parsed.isIncorrect
                    ? "incorrect"
                    : null,
              );
            }}
            onSave={handleSave}
          />
        )}

        {showSavedEvidence && (
          <IncorrectEvidenceSummary
            supportiveFacts={item.supportive_facts ?? []}
            contradictoryFacts={item.contradictory_facts ?? []}
            corrections={parsed.corrections}
            onEdit={openIncorrectForm}
            locations={chunkLocations}
          />
        )}

        {showSavedLogic && (
          <IncorrectLogicSummary
            correctLogic={parsed.correctLogic}
            onEdit={openIncorrectForm}
          />
        )}

        {showSavedComment && (
          <IncorrectCommentSummary
            comment={parsed.comment}
            onEdit={openIncorrectForm}
          />
        )}
      </div>

      <AiReasoningBox points={item.reasoning} />

      <EvidenceSection
        title="Supportive Evidence"
        tone="supportive"
        facts={item.supportive_facts ?? []}
        locations={chunkLocations}
      />
      <EvidenceSection
        title="Contradictory Evidence"
        tone="contradictory"
        facts={item.contradictory_facts ?? []}
        locations={chunkLocations}
      />
    </div>
  );
};
